import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, TemplateRef } from '@angular/core';
import { LeaseCardComponent } from '@app/common-components/lease-card/lease-card.component';
import { SelfHideConfiguration } from '@app/common-components/self-hide/self-hide-config-interface';
import { SelfHideComponent } from '@app/common-components/self-hide/self-hide.component';
import { AnnualLeaseService } from '@app/landowner-admin-shared/services/annual-lease.service';
import AnnualLease from '@app/models/annual-lease';
import AnnualLeaseCardViewModel from '@app/models/annual-lease-card-view-model';
import AnnualLeaseStatus from '@app/models/annual-lease-status';
import LeaseSeasonViewModel from '@app/models/lease-season-view-model';
import ListingViewModel from '@app/models/listing-view-model';
import SeasonLookup from '@app/models/season-lookup';
import SeasonLookupViewModel from '@app/models/season-lookup-view-model';
import TagViewModel from '@app/models/tag-view-model';
import ValidationResultModel from '@app/models/validation-result-model';
import {
  DateService,
  EntityService,
  ErrorHelperService,
  ListingService,
  LogService,
  NotificationService,
  UserService,
  ValueService,
} from '@app/services';
import { interval, Subject, Subscription } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

import LeaseSeasonGridContextType from '../lease-season-grid/lease-season-grid-context.enum';
import { LeaseSeasonGridComponent } from '../lease-season-grid/lease-season-grid.component';
import { SeasonLookupGridComponent } from '../season-lookup-grid/season-lookup-grid.component';
import { RangePipe } from '../../../common-components/pipes/range.pipe';
import { SeasonLookupFormComponent } from '../season-lookup-form/season-lookup-form.component';
import { LeaseCardComponent as LeaseCardComponent_1 } from '../../../common-components/lease-card/lease-card.component';
import { RowMetadataViewComponent } from '../../../common-components/row-metadata-view/row-metadata-view.component';
import { SecureIdCopyComponent } from '../../../common-components/secure-id-copy/secure-id-copy.component';
import { LeaseSeasonFormComponent } from '../lease-season-form/lease-season-form.component';
import { OADateInputComponent } from '../../../common-components/oadate-input/oadate-input.component';
import { PercentInputComponent } from '../../../common-components/percent-input/percent-input.component';
import { IntegerInputComponent } from '../../../common-components/integer-input/integer-input.component';
import { OAPopoverComponent } from '../../../common-components/oapopover/oapopover.component';
import { InputCharCounterComponent } from '../../../common-components/input-char-counter/input-char-counter.component';
import { ErrorListComponent } from '../../../common-components/error-list/error-list.component';
import { FormsModule } from '@angular/forms';
import { NgIf, NgFor, DatePipe } from '@angular/common';
import { NgbModal, NgbModalRef, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';

/**
 * A light-form component group for creating / editing an Annual Lease
 */
@Component({
    selector: "annual-lease",
    templateUrl: "./annual-lease.component.html",
    styleUrls: ["./annual-lease.component.less"],
    imports: [NgIf, FormsModule, ErrorListComponent, InputCharCounterComponent, NgFor, OAPopoverComponent, IntegerInputComponent, PercentInputComponent, OADateInputComponent, SeasonLookupGridComponent, LeaseSeasonGridComponent, LeaseSeasonFormComponent, SecureIdCopyComponent, RowMetadataViewComponent, LeaseCardComponent_1, NgbModalModule, SeasonLookupFormComponent, DatePipe, RangePipe]
})
export class AnnualLeaseComponent implements OnInit, OnDestroy {
  private readonly _componentName = 'AnnualLeaseComponent';
  private _lease: AnnualLease;
  private _listingId: number;
  private _valid = false;
  private _leaseCard: AnnualLeaseCardViewModel;
  private _showLeaseCard: boolean = true;

  private showDevComments$: Subscription;

  public listing: ListingViewModel;

  public LeaseSeasonGridContextType = LeaseSeasonGridContextType;

  @Input()
  public membershipFeeEnabled = false;

  @Output()
  public validChange = new EventEmitter<boolean>();

  @ViewChild("annualLeaseName") annualLeaseName: ElementRef;
  @ViewChild("annualLeaseDescription") annualLeaseDescription: ElementRef;

  @ViewChild("seasonLookupGrid") seasonLookupGrid: SeasonLookupGridComponent;

  @ViewChild("leaseSeasonGrid") leaseSeasonGrid: LeaseSeasonGridComponent;

  @ViewChild("addSeasonModal") addSeasonModal: TemplateRef<any>;
  modal: NgbModalRef;
  @ViewChild("selfHide", { static: true }) selfHide: SelfHideComponent;

  @ViewChild("leaseCardElement") leaseCardElement: LeaseCardComponent;

  public proRateEnabled = false;

  public renewalEnabled = false;

  public maximumParticipantCount = 100;

  public availableStatus: AnnualLeaseStatus[];

  public fieldValidationResult: Record<string, ValidationResultModel> = {};

  /**
   * Subject to control observable completion
   */
  // Add:

  private unSubSub$ = new Subject<void>();

  public dateValidation: ValidationResultModel;

  public validations: ValidationResultModel;

  public selectedSeasonLookup: SeasonLookup;

  public seasonTagButtons: TagViewModel[] = [
    {
      name: 'SeasonLookup-VA',
      id: null,
      description: null,
    },
    {
      name: 'Deer-TX',
      id: null,
      description: null,
    },
    {
      name: 'Deer-SC',
      id: null,
      description: null,
    },
    {
      name: 'Deer-NC',
      id: null,
      description: null,
    },
    {
      name: 'Deer-VA',
      id: null,
      description: null,
    },
  ];

  // Configures the success message that disappears after 5 sec.
  public successMessageSelfHideConfiguration: SelfHideConfiguration = {
    startImmediately: true,
    visibleMilliseconds: 5000,
    hideAfter: null,
  };

  public renewalNotificationDate: Date;
  seasonLookupPickerMessage: string;
  public retrieving: boolean;
  public refreshingCard: boolean;

  public get isEdit(): boolean {
    return this.lease && this.lease.id > 0;
  }

  public showDevComments: boolean;

  constructor(
    private log: LogService,
    private notify: NotificationService,
    private listingService: ListingService,
    private dateService: DateService,
    private valueHelper: ValueService,
    private userService: UserService,
    private errorHelper: ErrorHelperService,
    private annualLeaseService: AnnualLeaseService,
    private entityService: EntityService,
    private modalService: NgbModal
  ) {
    this.showDevComments$ = log.showDevComments.subscribe((x) => (this.showDevComments = x));

    this.entityService
      .getAll('AnnualLeaseStatus')
      .pipe(takeUntil(this.unSubSub$))
      .subscribe((data) => {
        console.log(`${this._componentName}.CTOR - 39db7 - getAll("AnnualLeaseStatus") data=`, data);
        if (data) {
          this.availableStatus = data.entities.filter((x) => x.name !== 'Purchased') as AnnualLeaseStatus[];
        }
      });
  }

  ngOnInit() {
    if (this.lease) {
      this.onChange('name', this.lease.name);
      this.validateServiceFeeAmount(this.lease.feeAmount);
    }

    this.eval();
  }

  ngOnDestroy(): void {
    this.unSubSub$.next(void 0);
    this.unSubSub$.complete();

    if (this.showDevComments$) {
      this.showDevComments$.unsubscribe();
    }
  }
  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  public get lease(): AnnualLease {
    return this._lease;
  }

  @Input()
  public set lease(v: AnnualLease) {
    console.log(`${this._componentName}.lease - set v=`, v);

    this._lease = v;

    if (v) {
      this.listingId = v.listingId;
    }
  }

  public get showLeaseCard(): boolean {
    return this._showLeaseCard;
  }
  public set showLeaseCard(v: boolean) {
    this._showLeaseCard = v;
  }

  private setLeaseCard() {
    let x: AnnualLeaseCardViewModel = {
      ...this.lease,
      leaseSecureAlternateId: this.lease.secureAlternateId,
      status: null,
      isPurchased: false,
      canPurchase: true,
      canPurchaseReason: null,
      title: null,
      leaseSeasons: null,
    };

    if (this.leaseSeasonGrid && this.leaseSeasonGrid.leaseSeasons) {
      x.leaseSeasons = this.leaseSeasonGrid.leaseSeasons;

      console.log(`${this._componentName}.leaseCard - get - leaseSeasons:`, x.leaseSeasons);
    } else {
      console.log(`${this._componentName}.leaseCard - get - leaseSeason set to empty array`);
      x.leaseSeasons = [];
    }

    this.leaseCard = x;
  }

  public get leaseCard(): AnnualLeaseCardViewModel {
    return this._leaseCard;
  }
  public set leaseCard(v: AnnualLeaseCardViewModel) {
    this._leaseCard = v;
    console.log(`${this._componentName}.leaseCard - set v=`, v);
  }

  public get addSeasonsToLeaseEnabled(): boolean {
    if (this.seasonLookupGrid && this.seasonLookupGrid.selectedSeasons && this.seasonLookupGrid.selectedSeasons.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  public get listingId(): number {
    return this._listingId;
  }

  public set listingId(v: number) {
    console.log(`${this._componentName}.listingId set - 39db7 -  listingId=${v}`);

    if (v === undefined) {
      
    }

    if (this.listingId && this.listingId === v) {
      this._listingId = v;
    } else {
      this._listingId = v;
      this.retrieveListing(v);
    }

    console.log(`${this._componentName}.listingId set - 39db7 - listingId=${v}`);
  }

  public get valid(): boolean {
    return this._valid;
  }

  public set valid(v: boolean) {
    this._valid = v;
  }

  public get isAdmin(): boolean {
    return this.userService.isAdmin;
  }

  private retrieveListing(listingId: number) {
    console.log(`${this._componentName}.retrieveListing - 39db7 - listingId=`, listingId);

    if (!listingId) return;

    this.retrieving = true;

    this.listingService.getAsViewModel(listingId).subscribe(
      (result) => {
        console.log(`${this._componentName}.retrieveListing.getAsViewModel - 39db7 - result:`, result);

        this.listing = result;

        this.eval();

        this.retrieving = false;
      },
      (err) => {
        console.error(err);
        
        this.retrieving = false;
        this.notify.error('A subscription error occured', null);
      }
    );
  }

  public consolePrint() {
    console.log(`${this._componentName}.consolePrint - 39db7 - lease=`, this.lease);

    console.log(`${this._componentName}.consolePrint - 39db7 - validations=`, this.validations);
    console.log(`${this._componentName}.consolePrint - 39db7 - dateValidation=`, this.dateValidation);

    console.log(`${this._componentName}.consolePrint - 39db7 - valid=`, this.valid);
  }

  /**
   * Gets the selected season lookup items in from the season grid component
   */
  public get selectedSeasons(): SeasonLookupViewModel[] {
    if (!this.leaseSeasonGrid || !this.leaseSeasonGrid.rows) {
      return null;
    }

    return this.leaseSeasonGrid.rows.filter((x) => x.selected);
  }

  /**
   * Gets all of the seasons regardless of whether they are selected
   */
  public get allSeasons(): SeasonLookupViewModel[] {
    if (!this.leaseSeasonGrid || !this.leaseSeasonGrid.rows) {
      return null;
    }

    return this.leaseSeasonGrid.rows;
  }

  /**
   * The lease season rows in the Lease Season Grid
   */
  public get leaseSeasons(): LeaseSeasonViewModel[] {
    if (this.leaseSeasonGrid && this.leaseSeasonGrid.leaseSeasons) {
      return this.leaseSeasonGrid.leaseSeasons;
    }
  }

  /**
   * Evaluates if the data in the component (this.lease) is valid and updates the "valid" BehaviorSubject
   * @returns
   */
  public eval() {
    console.log(`${this._componentName}.eval()`);

    let b = true;

    let validations = {
      issues: [],
    } as ValidationResultModel;

    if (!this.lease) {
      b = false;
      validations.issues.push('Empty lease property.');
    } else {
      if (!this.lease.name || !this.lease.description) {
        b = false;
        validations.issues.push('Lease requires a name and description.');
      }
    }

    if (this.leaseSeasonGrid && this.leaseSeasonGrid.leaseSeasons) {
      if (!this.leaseSeasonGrid.leaseSeasons.some((x) => x)) {
        b = false;
        validations.issues.push('No seasons selected.');
      } else {
        var conflictSeasons = this.leaseSeasonGrid.leaseSeasons.filter((x) => x.hasConflict);
        if (conflictSeasons.some((x) => x.hasConflict)) {
          b = false;
          conflictSeasons.forEach((conflict) => {
            validations.issues.push(`Season '${conflict.name}' has a conflict that requires resolving.`);
          });
        }
      }
    } else {
      b = false;
      validations.issues.push('No seasons selected.');
    }

    this.dateValidation = null;

    var dateValidations = {
      isValid: true,
      issues: [],
    } as ValidationResultModel;

    if (this.lease && !this.lease.startDateTime) {
      dateValidations.isValid = false;
      dateValidations.issues.push('Available Date not set.');
    }

    if (this.lease && !this.lease.endDateTime) {
      dateValidations.isValid = false;
      dateValidations.issues.push('End Date not set.');
    }

    if (this.lease && !this.lease.renewalDate) {
      dateValidations.isValid = false;
      dateValidations.issues.push('Renewal Date not set.');
    }

    if (this.lease && this.lease.startDateTime && this.lease.endDateTime && this.lease.renewalDate) {
      if (this.dateService.isDateGreaterThan(this.lease.startDateTime, this.lease.endDateTime)) {
        dateValidations.isValid = false;
        dateValidations.issues.push("Available Date cannot occur after the 'End Date'.");
      }

      if (this.dateService.isDateGreaterThan(this.lease.renewalDate, this.lease.endDateTime)) {
        dateValidations.isValid = false;
        dateValidations.issues.push("Renewal Date cannot occur after the 'End Date'.");
      }

      if (this.dateService.isDateGreaterThan(this.lease.startDateTime, this.lease.renewalDate)) {
        dateValidations.isValid = false;
        dateValidations.issues.push("Renewal Date cannot occur before the 'Available Date'.");
      }
    }

    this.dateValidation = dateValidations;

    if (!dateValidations.isValid) {
      b = false;
    }

    if (this.leaseSeasonGrid && this.leaseSeasonGrid.leaseSeasons) {
      this.leaseSeasonGrid.leaseSeasons.forEach((leaseSeason) => {
        if (
          !this.dateService.isWithin(leaseSeason.startDateTime, leaseSeason.endDateTime, this.lease.startDateTime, this.lease.endDateTime)
        ) {
          b = false;
          validations.issues.push(`Season (${leaseSeason.name}) dates must occur between lease start and lease end dates.`);
        }
      });
    }

    validations.isValid = b;

    this.validations = validations;

    console.log(`${this._componentName}.eval - 39db7 - `);

    if (this.fieldValidationResult['name'] && !this.fieldValidationResult['name'].isValid) {
      b = false;
    }

    if (this.fieldValidationResult['feeAmount'] && !this.fieldValidationResult['feeAmount'].isValid) {
      b = false;
    }

    if (this.valid != b) {
      console.log(`${this._componentName}.eval emitting - 39db7 -  ${b}`);

      this.validChange.emit(b);
      this.valid = b;
    }

    this.setLeaseCard();
    return b;
  }

  public onRefreshCardClick() {
    this.refreshingCard = true;
    this.setLeaseCard();

    interval(3400)
      .pipe(startWith(0))
      .subscribe(() => {
        this.refreshingCard = false;
      });
  }

  public onPriceChange(price: number, propertyName: string) {
    console.log(`${this._componentName}.onPriceChange propertyName=${propertyName}, price=`, price);




    if (this.valueHelper.equalsIgnoringCase(propertyName, 'feeAmount')) {
      this.validateServiceFeeAmount(price);
      this.eval();
    }

    if (this.leaseCardElement) {
      this.leaseCardElement.getCosts();
    }
  }

  /**
   * Validates the fee amount and updates the fieldValidationResult["feeAmount"] value.  
   * NOTE: this method does not call eval()
   * @param amount 
   */
  private validateServiceFeeAmount(amount:number){
    if (!amount) {
      this.fieldValidationResult['feeAmount'] = {
        isValid: false,
        issues: ['Service fee must be greater than zero'],
      } as ValidationResultModel;
    }else{
      this.fieldValidationResult['feeAmount'] = {
        isValid: true,
        issues: [],
      } as ValidationResultModel;
    }      
  }

  public onChange(propertyName: string, value: string) {
    console.log(`${this._componentName}.onChange - 39db7 - value:`, value);

    if (!value) return;

    if (!this.isEdit && this.valueHelper.equalsIgnoringCase(propertyName, 'name')) {
      this.annualLeaseService.checkValidityForNewAnnualLease(this.listingId, 'name', value).subscribe(
        (result) => {
          console.log(`${this._componentName} - 39db7 - subscription result=`, result);
          this.fieldValidationResult['name'] = result;
          this.eval();
        },
        (err) => {
          console.error(err);

          const msg = this.errorHelper.getErrorMessage(err) || 'A subscription error occured';

          console.error(msg);

          
          this.notify.error(msg, null);
        }
      );
    }

    this.eval();
  }

  public onDateChange(d: Date, propertyName: string) {
    console.log(`${this._componentName}.onDateChange - ${propertyName} - 39db7 -  d`, d);

    this.eval();

    if (propertyName === 'renewalDate') {
      this.updateRenewalNotificationDate(d, this.lease.autoRenewalNotificationDays);
    }
  }

  private updateRenewalNotificationDate(d: Date, autoRenewalNotificationDays: number) {
    this.renewalNotificationDate = this.dateService.addDays(d, autoRenewalNotificationDays * -1);
  }

  public onLeaseSeasonChange(items: LeaseSeasonViewModel) {
    console.log(`${this._componentName}.onLeaseSeasonChange - 39db7 - items:`, items);

    this.eval();
  }

  public onCreateNewSeasonClick() {
    console.log(`${this._componentName}.onCreateNewSeasonClick - 39db7 - `);

    this.selectedSeasonLookup = {
      createdBy: this.userService.userId,
      createdOn: this.dateService.nowUTC,
      name: null,
      secureAlternateId: this.valueHelper.generateUUID(),
      startDateTime: this.dateService.nowUTC,
      endDateTime: this.dateService.addMonths(this.dateService.nowUTC, 1),
    } as SeasonLookup;

    this.modal = this.modalService.open(this.addSeasonModal);
  }

  public onSeasonLookupAdd(evt: any) {
    console.log(`${this._componentName}.onSeasonLookupAdd - 39db7 - evt:`, evt);

    this.modal.close();

    this.leaseSeasonGrid.retrieve();
  }

  public onCancelSeasonLookup() {
    this.selectedSeasonLookup = null;
    this.modal.close();
  }

  public onAutoRenewalNotificationDaysValueChange(v: number) {
    console.log(`${this._componentName}.onAutoRenewalNotificationDaysValueChange - 39db7 - v:`, v);

    this.updateRenewalNotificationDate(this.dateService.parseDate(this.lease.renewalDate), v);
  }

  public onSeasonLookupSelected(item: SeasonLookupViewModel) {
    console.log(`${this._componentName}.onSeasonLookupSelected item=`, item);

    this.leaseSeasonGrid.addLeaseSeason(item);

    this.seasonLookupPickerMessage = `${item.name} added to your lease.`;

    if (this.selfHide) {
      this.selfHide.start();
    }

    this.eval();
  }

  public onSeasonTagButtonClick(item: TagViewModel) {
    console.log(`${this._componentName}.onSeasonTagButtonClick item=`, item);

    this.annualLeaseService
      .getSeasonLookupByTag(item.name)
      .pipe(takeUntil(this.unSubSub$))
      .subscribe(
        (items) => {
          // items

          this.leaseSeasonGrid.addLeaseSeasons(items);
        },
        (err) => {
          console.error(err);

          const msg = this.errorHelper.getErrorMessage(err) || 'A subscription error occured';

          console.error(msg);

          
          this.notify.error(msg, null);
        }
      );
  }

  /**
   * Handler for the click of the lease purchase button.
   */
  public onLeasePurchase() {
    this.notify.success('Purchase button was clicked.');
  }

  public onSeasonsToLease() {
    console.log(`${this._componentName}. onSeasonsToLease()`);

    this.leaseSeasonGrid.addLeaseSeasons(this.seasonLookupGrid.selectedSeasons);
  }
}
